import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router";
import { Trash2, AlertTriangle } from "lucide-react";
import { useAppContext } from "@/providers";
import { useToast } from "@/components/ui/toast";
import { Input } from "@/components/ui/input";
import { EditorialModal, ModalSection } from "@/components/ui/editorial-modal";
import { t } from "@/lib/i18n/messages";
import { deleteInitiative } from "@/lib/api/initiatives";
import { cn } from "@/lib/utils";
import type { Initiative } from "@/types/initiative";

interface DeleteInitiativeDialogProps {
  initiative: Initiative;
  onClose: () => void;
}

export function DeleteInitiativeDialog({
  initiative,
  onClose,
}: DeleteInitiativeDialogProps) {
  const { locale } = useAppContext();
  const isRTL = locale === "ar";
  const { toast } = useToast();
  const navigate = useNavigate();
  const [deleting, setDeleting] = useState(false);
  const [error, setError] = useState("");
  const [confirmText, setConfirmText] = useState("");
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    inputRef.current?.focus();
  }, []);

  const title =
    locale === "ar"
      ? initiative.title_ar || initiative.title
      : initiative.title_en || initiative.title;

  const isConfirmed = confirmText.trim() === title.trim();
  const canDelete = isConfirmed && !deleting;

  const handleDelete = async () => {
    if (!isConfirmed) return;
    setError("");
    setDeleting(true);
    try {
      const res = await deleteInitiative(initiative.id);
      if (res.success) {
        toast(
          isRTL
            ? "تم حذف المبادرة بنجاح"
            : "Initiative deleted successfully",
          "success",
        );
        void navigate("/dashboard/initiatives");
      } else {
        setError(res.message ?? t(locale, "error"));
      }
    } catch {
      setError(t(locale, "error"));
    } finally {
      setDeleting(false);
    }
  };

  return (
    <EditorialModal
      isRTL={isRTL}
      onClose={onClose}
      heroIcon={Trash2}
      eyebrow={isRTL ? "حذف · المبادرة" : "DELETE · INITIATIVE"}
      title={t(locale, "deleteInitiativeConfirmTitle")}
      subtitle={
        isRTL
          ? "هذا الإجراء لا يمكن التراجع عنه."
          : "This action cannot be undone."
      }
      tone="danger"
      maxWidthClass="max-w-md"
      closeLabel={t(locale, "close")}
      footer={
        <>
          <button
            type="button"
            onClick={onClose}
            disabled={deleting}
            className="flex-1 h-12 rounded-pill border border-border text-foreground text-[14px] font-bold hover:bg-muted transition-colors disabled:opacity-50"
          >
            {t(locale, "cancelAction")}
          </button>
          <button
            type="button"
            onClick={() => void handleDelete()}
            disabled={!canDelete}
            className={cn(
              "flex-1 h-12 rounded-pill inline-flex items-center justify-center gap-2",
              "bg-red-600 hover:bg-red-700 text-white",
              "text-[14px] font-bold shadow-sm transition-all active:scale-[0.98]",
              "disabled:opacity-40 disabled:cursor-not-allowed",
            )}
          >
            {deleting ? (
              <span className="inline-flex items-center gap-2">
                <span className="w-4 h-4 rounded-full border-2 border-white/40 border-t-white animate-spin" />
                <span>{isRTL ? "جارٍ الحذف..." : "Deleting..."}</span>
              </span>
            ) : (
              <>
                <Trash2 className="w-4 h-4" strokeWidth={2} />
                <span>{t(locale, "deleteInitiative")}</span>
              </>
            )}
          </button>
        </>
      }
    >
      <ModalSection
        num="01"
        title={isRTL ? "تنبيه" : "Heads up"}
        tone="danger"
      >
        <div className="rounded-2xl border border-red-200 bg-red-50 p-4 flex gap-3 items-start">
          <span
            aria-hidden
            className="shrink-0 inline-flex items-center justify-center w-8 h-8 rounded-lg bg-red-100 text-red-600"
          >
            <AlertTriangle className="w-4 h-4" strokeWidth={2} />
          </span>
          <div className="space-y-1">
            <p className="text-[13px] text-foreground">
              {t(locale, "deleteInitiativeConfirmMsg")}
            </p>
            <p className="font-display font-bold text-foreground truncate">
              &ldquo;{title}&rdquo;
            </p>
          </div>
        </div>
      </ModalSection>

      <ModalSection
        num="02"
        title={isRTL ? "أكِّد بالكتابة" : "Type to confirm"}
        tone="danger"
        hint={isRTL ? "اكتب اسم المبادرة" : "Type the initiative name"}
      >
        <Input
          ref={inputRef}
          dir={isRTL ? "rtl" : "ltr"}
          value={confirmText}
          onChange={(e) => setConfirmText(e.target.value)}
          placeholder={title}
          aria-label={isRTL ? "اسم المبادرة" : "Initiative name"}
        />
        {error && (
          <p
            className="font-sans text-[10px] tracking-widest text-red-500 uppercase"
            role="alert"
          >
            {error}
          </p>
        )}
      </ModalSection>
    </EditorialModal>
  );
}
